import type { NotificationType } from 'types';
import { Fragment } from 'react';
import { Switch } from '@/components/ui/switch';
import { NOTIFICATION_TYPE_GROUPS } from 'definitions';
import { SERVER_SIDE_NOTIFICATION_TYPES, canUseServerSide } from 'definitions/server-side-access';
import { useAuth } from 'hooks/useAuth';
import { notificationText } from 'helpers/notification-text';

const isServerSideType = (type: NotificationType) =>
  (SERVER_SIDE_NOTIFICATION_TYPES as readonly string[]).includes(type);

type NotificationMatrixProps = {
  settings: Record<string, boolean> | undefined;
  onToggle: (type: NotificationType, enabled: boolean) => void;
  disabled?: boolean;
  // Shown in place of the switches when the channel cannot be used yet, which is
  // the un-connected Slack case.
  lockedReason?: string;
};

/**
 * One column of switches over the grouped notification catalogue.
 *
 * Both screens render this: the Account screen drives the user's own email
 * preferences, Organisation settings drives the company's Slack routing. The row
 * labels come from helpers/notification-text, the same module the panel, the email
 * and the Slack post read, so a renamed event renames everywhere at once.
 */
export const NotificationMatrix = ({ settings, onToggle, disabled, lockedReason }: NotificationMatrixProps) => {
  // Server key events are not sent while server-side is staff-only, so they are not offered either.
  const { user } = useAuth();
  const showServerSide = canUseServerSide(user);

  if (!settings) {
    return (
      <div className="space-y-2">
        {[0, 1, 2, 3].map((row) => (
          <div key={row} className="bg-field rounded-card h-10 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {lockedReason ? <p className="supporting m-0">{lockedReason}</p> : null}

      {NOTIFICATION_TYPE_GROUPS.map((group) => (
        <Fragment key={group.key}>
          <div className="space-y-1">
            <h4 className="micro-label m-0">{group.label}</h4>
            <p className="supporting m-0">{group.description}</p>
          </div>

          <div className="border-line rounded-card flex flex-col border">
            {group.types
              .filter((type) => showServerSide || !isServerSideType(type))
              .map((type) => {
                const id = `notification-${type}`;

                return (
                  <div
                    key={type}
                    className="border-line hover:bg-hover flex items-center justify-between gap-4 border-b px-3 py-2.5 transition-colors duration-150 last:border-b-0"
                  >
                    <label htmlFor={id} className="text-foreground cursor-pointer text-meta">
                      {notificationText(type).label}
                    </label>
                    <Switch
                      id={id}
                      checked={!!settings[type]}
                      disabled={disabled}
                      onCheckedChange={(checked) => onToggle(type as NotificationType, checked)}
                    />
                  </div>
                );
              })}
          </div>
        </Fragment>
      ))}
    </div>
  );
};
